import React, { useState } from 'react';
import styled from 'styled-components';

import Card from '@components/Card';
import Select from '@components/Select';
import useAPI from '@hooks/useAPI';
import Button from '@shared/Button';
import Modal from '@shared/Modal';
import Spinner from '@shared/Spinner';

import {
  ALL_ENVIRONMENTS,
  getCurrentNamespace,
  getEnvLabel,
  ModalContainer,
  SummarySection,
  ErrorItem,
  ErrorRuleName,
  ErrorDetail,
  DirectionLabel,
  CloseButtonRow,
  DiffSummary,
  RawJsonView,
} from './RuleDiffShared';

const Container = styled.div`
  width: 70%;
  margin: auto;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 12px;
`;

const SelectContainer = styled.div`
  width: 250px;
`;

const HelpText = styled.p`
  font-size: 13px;
  color: #888;
  margin: 0 0 16px 0;
`;

const CurrentEnvLabel = styled.span`
  font-size: 13px;
  color: #555;
`;

/* ── Modal ── */

function EnvDiffModal({ response, isLoading, error, handleClose }) {
  const hasResponse = response && !error;
  const source = response?.source || '';
  const destination = response?.destination || '';

  return (
    <Modal
      id='env-diff-modal'
      open
      toggle={handleClose}
      closeOnClickOutside
      maxWidth={900}
    >
      <Card title='Environment Rule Diff' shadow>
        <ModalContainer>
          {isLoading && <Spinner center />}

          {error && (
            <SummarySection>
              <ErrorItem>
                <ErrorRuleName>Request Error</ErrorRuleName>
                <ErrorDetail>{error}</ErrorDetail>
              </ErrorItem>
            </SummarySection>
          )}

          {hasResponse && (
            <>
              <DirectionLabel>
                Comparing rules from <strong>{source}</strong> against{' '}
                <strong>{destination}</strong>
              </DirectionLabel>
              <DiffSummary
                diff={response}
                emptyMessage='No rule differences found between the two environments.'
              />
              <RawJsonView src={response} />
            </>
          )}

          <CloseButtonRow>
            <Button onClick={handleClose}>Close</Button>
          </CloseButtonRow>
        </ModalContainer>
      </Card>
    </Modal>
  );
}

/* ── Main component ── */

export default function EnvironmentRuleDiff() {
  const currentNamespace = getCurrentNamespace();
  const currentLabel = getEnvLabel(currentNamespace);

  const sourceOptions = ALL_ENVIRONMENTS.filter(
    ([key]) => key !== currentNamespace
  );

  const [selectedSource, setSelectedSource] = useState('');
  const [diffParams, setDiffParams] = useState([]);
  const [showModal, setShowModal] = useState(false);

  const diffResponse = useAPI(...diffParams);

  const handleCompare = () => {
    if (!selectedSource) return;
    setShowModal(true);
    setDiffParams([
      'post',
      'ScreenConsoleApi',
      `config/${process.env.REACT_APP_CUSTOMER_ID}/rules/diff`,
      Date.now().toString(),
      false,
      '',
      false,
      { source: selectedSource },
    ]);
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  return (
    <Container>
      <HelpText>
        Compare rules from another environment against this environment
        to preview what would differ.
      </HelpText>

      <Row>
        <SelectContainer>
          <Select
            options={sourceOptions}
            value={selectedSource}
            onChange={e => setSelectedSource(e.target.value)}
            placeholder='Select source environment'
            style={{ height: '43px' }}
          />
        </SelectContainer>
        <CurrentEnvLabel>
          → Current environment: <strong>{currentLabel}</strong>
        </CurrentEnvLabel>
      </Row>

      <Row>
        <Button onClick={handleCompare} disabled={!selectedSource}>
          Compare
        </Button>
      </Row>

      {showModal && (
        <EnvDiffModal
          response={diffResponse.response}
          isLoading={diffResponse.isLoading}
          error={diffResponse.error}
          handleClose={handleCloseModal}
        />
      )}
    </Container>
  );
}
